import ShButton from "@/components/Helpers/ShButton";
import {
  hAmountOfSmsNeeded,
  isGsmMessage,
  getGsmMessageDoubleCharactersAmount,
  replaceAllPolishCharacters
} from "@/utils/helpers";
import { messageLimits } from "@/utils/consts";

// @vue/component
export default {
  name: "SmsTemplates",

  components: {
    ShButton
  },

  props: {
    projectsList: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      action: "create",
      form: {
        project: "",
        template: "",
        smsContent: ""
      },
      smsContentCount: 0,
      arePolishCharactersRemoved: false,
      rules: {
        project: [{ required: true, message: "Projekt jest wymagany.", trigger: "blur" }],
        template: [
          { required: true, message: "Nazwa szablonu jest wymagana.", trigger: "blur" },
          { validator: this.__validateTemplateName, trigger: "blur" }
        ],
        smsContent: [
          {
            required: true,
            message: "Treść wiadomości jest wymagana.",
            trigger: ["blur", "change"]
          },
          {
            min: 1,
            max: 918,
            message: "Treść wiadomości musi mieć min. 1 i max. 918 znaków.",
            trigger: "blur"
          }
        ]
      }
    };
  },

  computed: {
    smsTemplatesList() {
      return this.$store.getters["sms/templatesList"];
    },
    smsCounter() {
      return hAmountOfSmsNeeded(this.smsContentCount, this.smsContentHasSpecialCharacters);
    },
    isLoading() {
      return this.$store.getters["global/pendingAsyncStoreActions"].includes([
        "sms/editSmsTemplate",
        "sms/createSmsTemplate",
        "sms/removeSmsTemplate"
      ]);
    },
    isTemplatesLoading() {
      return this.$store.getters["global/pendingAsyncStoreActions"].includes([
        "sms/getSmsTemplatesList"
      ]);
    },
    smsContentLimit() {
      if (this.arePolishCharactersRemoved) return messageLimits.noSpecialCharacters;
      return this.smsContentHasSpecialCharacters
        ? messageLimits.withSpecialCharacters
        : messageLimits.noSpecialCharacters;
    },
    smsContentHasSpecialCharacters() {
      if (this.arePolishCharactersRemoved) return false;
      return !isGsmMessage(this.form.smsContent);
    }
  },

  watch: {
    action: {
      handler(newVal) {
        this.form.project = "";
        this.form.template = "";
        this.form.smsContent = "";
      },
      immediate: true
    },
    arePolishCharactersRemoved: {
      handler(val) {
        const specialCharacters = getGsmMessageDoubleCharactersAmount(this.form.smsContent);
        if (val) {
          this.smsContentCount = this.form.smsContent.length + specialCharacters;
        } else {
          const numberOfSpecialCharacters = this.smsContentHasSpecialCharacters
            ? 0
            : specialCharacters;
          this.smsContentCount = this.form.smsContent.length + numberOfSpecialCharacters;
        }
      }
    }
  },

  methods: {
    handleSmsContentInput(value) {
      if (this.smsContentCount >= this.smsContentLimit) {
        if (value >= this.form.smsContent) {
          return;
        }
      }
      this.form.smsContent = value;
      const numberOfSpecialCharacters = this.smsContentHasSpecialCharacters
        ? 0
        : getGsmMessageDoubleCharactersAmount(value);
      this.smsContentCount = value.length + numberOfSpecialCharacters;
    },
    selectProject(projectUuid) {
      this.$store.dispatch("sms/getSmsTemplatesList", projectUuid);
    },
    selectTemplate(templateUuid) {
      this.form.smsContent = this.smsTemplatesList.filter(
        item => item.uuid === templateUuid
      )[0].text;
    },
    async handleEditTemplate() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          const params = {
            project_uuid: this.form.project,
            template_uuid: this.form.template,
            text: this.form.smsContent
          };
          const responseStatus = await this.$store.dispatch("sms/editSmsTemplate", params);
          if (responseStatus === 200) {
            this.$message({
              type: "success",
              message: "Zmiany zostały zapisane."
            });
          } else {
            this.$message({
              type: "error",
              message: "Wystąpił błąd podczas zapisywania zmian"
            });
          }
          await this.$store.dispatch("sms/getSmsQueueList");
          this.clearForm();
        }
      });
    },
    async handleRemoveTemplate() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          const params = {
            project_uuid: this.form.project,
            template_uuid: this.form.template
          };
          const responseStatus = await this.$store.dispatch("sms/removeSmsTemplate", params);
          if (responseStatus === 200) {
            this.$message({
              type: "success",
              message: "Szablon został usunięty."
            });
          } else {
            this.$message({
              type: "error",
              message: "Wystąpił błąd podczas usuwania szablonu"
            });
          }
          await this.$store.dispatch("sms/getSmsQueueList");
          this.clearForm();
        }
      });
    },
    async handleCreateTemplate() {
      this.$refs.form.validate(async valid => {
        if (this.smsContentCount > this.smsContentLimit) {
          this.$message({
            type: "error",
            message: "Długość wiadomości wykracza poza limit"
          });
          return;
        }
        if (valid) {
          const params = {
            project_uuid: this.form.project,
            description: this.form.template,
            text: this.arePolishCharactersRemoved
              ? replaceAllPolishCharacters(this.form.smsContent)
              : this.form.smsContent
          };
          const responseStatus = await this.$store.dispatch("sms/createSmsTemplate", params);
          if (responseStatus === 200) {
            this.$message({
              type: "success",
              message: "Nowy szablon został zapisany."
            });
          } else {
            this.$message({
              type: "error",
              message: "Wystąpił błąd podczas zapisywania szablonu"
            });
          }
          await this.$store.dispatch("sms/getSmsQueueList");
          this.clearForm();
        }
      });
    },
    clearForm() {
      this.form.project = "";
      this.form.template = "";
      this.form.smsContent = "";
      this.smsContentCount = 0;
    },
    __validateTemplateName(rule, value, callback) {
      if (this.action === "create") {
        if (value.length < 1 || value.length > 30) {
          callback(new Error("Nazwa szablonu musi mieć min. 1 i max. 30 znaków."));
        } else {
          callback();
        }
      } else {
        callback();
      }
    }
  }
};
